/**
 * UsMap — the Coverage tab's choropleth (ADR-0101). Thin and presentational:
 * 50 static state paths (no DC — it lives in the page's small-state strip),
 * each filled by its depth bin and wired to the page's hover/pin handlers.
 *
 * The hairline `--background` stroke is load-bearing: without it, adjacent
 * zero-coverage states merge into one gray mass. No fill transitions by
 * design — chip toggles should read instantly, not animate.
 */

import {
  US_MAP_VIEWBOX,
  US_STATE_PATHS,
} from "@/assets/us-states-paths";
import { fillForDepth, type Depth } from "@/lib/coverage-derive";

export function UsMap({
  depths,
  onHover,
  onSelect,
}: {
  depths: Record<string, Depth>;
  onHover: (code: string) => void;
  onSelect: (code: string) => void;
}) {
  return (
    <svg
      viewBox={US_MAP_VIEWBOX}
      width="100%"
      role="img"
      aria-label="US coverage map"
      data-testid="coverage-map"
    >
      {Object.entries(US_STATE_PATHS).map(([code, d]) => (
        <path
          key={code}
          d={d}
          fill={fillForDepth(depths[code] ?? 0)}
          stroke="hsl(var(--background))"
          strokeWidth={1}
          className="cursor-pointer"
          data-testid={`coverage-state-${code}`}
          aria-label={code}
          onMouseEnter={() => onHover(code)}
          onClick={() => onSelect(code)}
        />
      ))}
    </svg>
  );
}
